import React, { useState } from 'react';
import { apiFetch } from '../../utils/api';
import SplitEditor, { getRemainingAmount } from './SplitEditor';

function AddExpenseModal({ isOpen, onClose, allUsers, groups, currentUser, onSuccess, initialGroupId, expenseToEdit }) {
  const isEditing = !!expenseToEdit;
  const [form, setForm] = useState(() => {
    if (isEditing) {
      const firstOwed = expenseToEdit.participants?.[0]?.amountOwed || 0;
      const isEquals = expenseToEdit.participants?.every(
        (p) => Math.abs(p.amountOwed - firstOwed) <= 0.05
      ) ?? true;

      return {
        description: expenseToEdit.description,
        amount: expenseToEdit.amount.toString(),
        paidByUserId: expenseToEdit.paidByUserId,
        splitType: isEquals ? 'equal' : 'exact',
      };
    }
    return {
      description: '',
      amount: '',
      paidByUserId: currentUser.id,
      splitType: 'equal',
    };
  });
  const [selectedGroupId, setSelectedGroupId] = useState(() => {
    if (isEditing) return expenseToEdit.groupId || '';
    return initialGroupId || '';
  });
  const [selectedParticipants, setSelectedParticipants] = useState(() => {
    if (isEditing) {
      return expenseToEdit.participants?.map((p) => p.userId) || [];
    }
    if (initialGroupId) {
      const group = groups.find((g) => g.id === initialGroupId);
      if (group) {
        return group.members.map((m) => m.userId);
      }
    }
    return [];
  });

  const users = selectedGroupId
    ? allUsers.filter(u => {
        const group = groups.find(g => g.id === selectedGroupId);
        return group ? group.members.some(m => m.userId === u.id) : false;
      })
    : allUsers;

  const handleGroupChange = (e) => {
    const newGroupId = e.target.value;
    setSelectedGroupId(newGroupId);
    
    if (newGroupId) {
      const group = groups.find(g => g.id === newGroupId);
      if (group) {
        const memberIds = group.members.map(m => m.userId);
        setSelectedParticipants(memberIds);
      }
    } else {
      setSelectedParticipants([]);
    }
  };
  const [exactAmounts, setExactAmounts] = useState(() => {
    if (isEditing) {
      const exacts = {};
      expenseToEdit.participants?.forEach((p) => {
        exacts[p.userId] = p.amountOwed.toString();
      });
      return exacts;
    }
    return {};
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);


  const remainingAmount = getRemainingAmount(form.amount, selectedParticipants, exactAmounts);
  const isRemainingZero = Math.abs(remainingAmount) < 0.01;
  const isSubmitDisabled = loading || (form.splitType === 'exact' && !isRemainingZero);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.description || !form.amount || parseFloat(form.amount) <= 0) {
      setError('Please provide a valid description and amount.');
      return;
    }

    if (selectedParticipants.length === 0) {
      setError('At least one participant must be selected.');
      return;
    }

    const totalAmount = parseFloat(form.amount);
    let participantsData = [];

    if (form.splitType === 'equal') {
      const share = totalAmount / selectedParticipants.length;
      participantsData = selectedParticipants.map((id) => ({
        userId: id,
        amountOwed: parseFloat(share.toFixed(2)),
      }));

      // Adjust rounding discrepancies
      const sum = participantsData.reduce((acc, curr) => acc + curr.amountOwed, 0);
      const diff = totalAmount - sum;
      if (Math.abs(diff) > 0.001 && participantsData.length > 0) {
        participantsData[0].amountOwed = parseFloat((participantsData[0].amountOwed + diff).toFixed(2));
      }
    } else {
      // Exact split logic
      let sum = 0;
      for (const id of selectedParticipants) {
        const val = parseFloat(exactAmounts[id]);
        if (isNaN(val) || val < 0) {
          setError('Please provide valid exact split amounts for all checked participants.');
          return;
        }
        sum += val;
        participantsData.push({
          userId: id,
          amountOwed: val,
        });
      }

      const diff = parseFloat((totalAmount - sum).toFixed(2));
      if (Math.abs(diff) >= 0.01) {
        setError(`Sum of exact splits (₹${sum.toFixed(2)}) must equal total amount (₹${totalAmount.toFixed(2)}). Remaining: ₹${diff.toFixed(2)}`);
        return;
      }
    }

    setLoading(true);
    try {
      const url = isEditing ? `/expenses/${expenseToEdit.id}` : '/expenses';
      const method = isEditing ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify({
          description: form.description,
          amount: totalAmount,
          paidByUserId: form.paidByUserId,
          participants: participantsData,
          groupId: selectedGroupId || null,
        }),
      });

      if (!res.ok) {
        throw new Error(isEditing ? 'Failed to update expense' : 'Failed to create expense');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || (isEditing ? 'Error updating expense.' : 'Error creating expense.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content animate-slide-up">
        <div className="modal-header">
          <h3>{isEditing ? '✏️ Edit Expense' : '➕ Add an Expense'}</h3>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              placeholder="e.g. Flight tickets, Dinner, Rent"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Group</label>
            <select value={selectedGroupId} onChange={handleGroupChange} disabled={isEditing}>
              <option value="">Non-group expense</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Amount (₹)</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Paid By</label>
              <select
                value={form.paidByUserId}
                onChange={(e) => setForm({ ...form, paidByUserId: e.target.value })}
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.id === currentUser.id ? 'You' : u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <SplitEditor
            users={users}
            currentUser={currentUser}
            amount={form.amount}
            splitType={form.splitType}
            onSplitTypeChange={(splitType) => setForm({ ...form, splitType })}
            selectedParticipants={selectedParticipants}
            onSelectedParticipantsChange={setSelectedParticipants}
            exactAmounts={exactAmounts}
            onExactAmountsChange={setExactAmounts}
          />

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitDisabled}>
              {loading ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Expense')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddExpenseModal;
